import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, NgForm, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { DialogConfirm, DialogConfirmData } from 'app/dialogs/confirm/dialog-confirm';
import { formatDate } from 'app/helpers/formatDate';
import { InlineBase64UploadAdapter } from 'app/helpers/inlineBase64UploadAdapter';
import { sortCompare } from 'app/helpers/sortCompare';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListJiraIssuesReq } from 'app/interfaces/requests/Exposed/exposedListJiraIssuesReq';
import { ExposedJiraAllowedValuesFieldsDTORes } from 'app/interfaces/responses/Exposed/exposedJiraAllowedValuesFieldsDTORes';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { JiraCreateIssueDTOReq } from 'app/interfaces/support/jira/jiraCreateIssueDTOReq';
import { JiraCreateIssueFieldsDescriptionDTOs } from 'app/interfaces/support/jira/jiraCreateIssueFieldsDescriptionDTOs';
import { JiraIssuesListRes } from 'app/interfaces/support/jira/jiraIssuesListRes';
import { JiraService } from 'app/services/api/jira/jira.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { HtmlToAdfService } from 'app/services/system/htmltoadf/htmltoadf.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { filter, startWith, take } from 'rxjs';

@Component({
    selector: 'app-assists-jira-ut',
    templateUrl: './assists-jira-ut.component.html',
    styleUrls: ['./assists-jira-ut.component.scss'],
})
export class AssistsJiraUtComponent implements OnInit, AfterViewInit {
    @ViewChild('sentinel', { static: false }) sentinel!: ElementRef;

    issues: Array<JiraIssuesListRes> = [];

    private observer!: IntersectionObserver;
    pageSize: number = 50;
    isLastPage: boolean = false;
    nextPageToken: string = null;
    orgRequestsColumns: string[] = ['key', 'title', 'created', 'status'];
    displayedData: MatTableDataSource<JiraIssuesListRes> = new MatTableDataSource();
    isLoading: boolean = true;
    loadSessionId: number = 0;
    // Keep track of if the initial fetch of issues is complete
    initialFetchComplete: boolean = false;
    descriptionEditor = ClassicEditor;
    descriptionTextHTML: string;
    navigation: FuseNavigationItem[];
    userDetails: ExposedReadUserDetailsRes = null;

    descriptionTooltip =
        'Tilgreina skal allar helstu upplýsingar s.s. ástæðu og fyrir hvern aðgangurinn er (ef hann er fyrir annan en innsendanda)';
    samthykkjandiTooltip = 'Aðili sem mun koma til með að yfirfara aðgangsbeiðnina';
    issueTypeId = '10003';
    issueProjectKey = 'UT';
    // Default value for approver is "10268", which is "Stafræn þróun og rekstur"
    customfield_10099 = '10268';

    systemDropDown: Array<DropDownItem> = [];
    accessTypeDropDown: Array<DropDownItem> = [];
    approverDropDown: Array<DropDownItem> = [];

    files: File[] = [];

    @ViewChild('ngForm') ngForm: NgForm;

    form: UntypedFormGroup;
    submitted: boolean = false;
    formEnabled: boolean = true;

    showAccessRequestForm: boolean = false;
    showMyRequests: boolean = false;

    constructor(
        private translationService: TranslationService,
        private dialog: MatDialog,
        public jiraService: JiraService,
        public loaderService: LoaderService,
        private alertsService: AlertsService,
        private _formBuilder: UntypedFormBuilder,
        public lsrauthService: LsrauthService,
        private htmlToAdf: HtmlToAdfService,
        public router: Router,
        public route: ActivatedRoute
    ) {
        this.htmlToAdf.preload();
    }

    ngOnInit(): void {
        this.lsrauthService.userDetails
            .pipe(
                filter((userDetails) => userDetails != null),
                take(1)
            )
            .subscribe((userDetails) => {
                this.userDetails = userDetails;
                this.maybeSetApprover.call(this);
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                startWith(null)
            )
            .subscribe(() => {
                this.handleCurrentRoute();
            });
    }

    ngAfterViewInit(): void {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                if (this.nextPageToken && !this.isLoading) {
                    this.observer.unobserve(entry.target);
                    this.fetchIssues();
                } else if (this.isLastPage) {
                    this.observer.disconnect();
                }
            });
        });
    }

    resetObserver() {
        if (!this.observer || !this.sentinel?.nativeElement) return;
        this.observer.disconnect();
        this.observer.observe(this.sentinel.nativeElement);
    }

    private handleCurrentRoute(): void {
        this.showMyRequests = this.router.url.includes('/minar-beidnir');
        this.showAccessRequestForm = this.router.url.includes('/skra-beidni');

        if (this.showMyRequests) {
            const createdIssueKey = this.route.snapshot.queryParamMap.get('created');

            this.resetList();
            this.fetchIssues(createdIssueKey ?? undefined);
            return;
        }

        if (this.showAccessRequestForm) {
            this.initForm();
        }
    }

    private mergeByKey(existing: any[], incoming: any[]) {
        const seen = new Set(existing.map((i) => i.key));
        const merged = [...existing];
        for (const item of incoming) {
            if (!seen.has(item.key)) {
                merged.push(item);
                seen.add(item.key);
            }
        }
        return merged;
    }

    // Map helper to prevent duplicating the mapping code
    private mapIssues(res: any): JiraIssuesListRes[] {
        return (res.issues ?? []).map(
            (issue) =>
                ({
                    created: issue.fields.created,
                    key: issue.key,
                    url: 'https://lsr.atlassian.net/servicedesk/customer/portal/3/' + issue.key,
                    title: issue.fields.summary,
                    reporterName: issue.fields.reporter.displayName,
                    status: issue.fields.status.name,
                    eaDate: null,
                    ownerName: null,
                    implementerName: null,
                    assistAmount: null,
                    assistAllocationDate: null,
                    requestTypeId: issue.fields.customfield_10010?.requestType?.id ?? null,
                }) as JiraIssuesListRes
        );
    }

    initForm() {
        this.submitted = false;
        this.formEnabled = true;

        this.listAllowedValues();
        this.descriptionTextHTML = '';
        this.files = [];
        this.descriptionEditor = ClassicEditor;

        this.form = this._formBuilder.group({
            summary: new UntypedFormControl('', [Validators.required]),
            description: new UntypedFormControl('', [Validators.required]),
            customfield_10068: new UntypedFormControl('', [Validators.required]),
            customfield_10099: new UntypedFormControl(''),
            customfield_10098: this._formBuilder.array([]),
        });
    }

    findUsersSvidsstjori() {
        try {
            if (this.userDetails.division.toLowerCase().includes('Áhættustýring'.toLowerCase())) {
                this.customfield_10099 = this.approverDropDown.find((x) =>
                    x.viewValue.toLowerCase().includes('Áhættustýring'.toLowerCase())
                ).value;
            } else if (this.userDetails.division.toLowerCase().includes('Eignastýring'.toLowerCase())) {
                this.customfield_10099 = this.approverDropDown.find((x) =>
                    x.viewValue.toLowerCase().includes('Eignastýring'.toLowerCase())
                ).value;
            } else if (this.userDetails.division.toLowerCase().includes('Fjármála'.toLowerCase())) {
                this.customfield_10099 = this.approverDropDown.find((x) =>
                    x.viewValue.toLowerCase().includes('Fjármála'.toLowerCase())
                ).value;
            } else if (this.userDetails.division.toLowerCase().includes('Lánasvið'.toLowerCase())) {
                this.customfield_10099 = this.approverDropDown.find((x) =>
                    x.viewValue.toLowerCase().includes('Lánasvið'.toLowerCase())
                ).value;
            } else if (this.userDetails.division.toLowerCase().includes('Lífeyrissvið'.toLowerCase())) {
                this.customfield_10099 = this.approverDropDown.find((x) =>
                    x.viewValue.toLowerCase().includes('Lífeyrissvið'.toLowerCase())
                ).value;
            } else if (this.userDetails.division.toLowerCase().includes('Framkvæmda'.toLowerCase())) {
                this.customfield_10099 = this.approverDropDown.find((x) =>
                    x.viewValue.toLowerCase().includes('Framkvæmda'.toLowerCase())
                ).value;
            } else {
                // Stafræn þróun og rekstur is the default value if the user is not an employee
                this.customfield_10099 = this.approverDropDown.find(
                    (x) =>
                        x.viewValue.toLowerCase().includes('Stafræn þróun'.toLowerCase()) ||
                        x.viewValue.toLowerCase().includes('Stafrænnar þróunar'.toLowerCase())
                ).value;
            }
        } catch {
            this.customfield_10099 = this.approverDropDown.find(
                (x) =>
                    x.viewValue.toLowerCase().includes('Stafræn þróun'.toLowerCase()) ||
                    x.viewValue.toLowerCase().includes('Stafrænnar þróunar'.toLowerCase())
            ).value;
        }
        this.form.get('customfield_10099')?.setValue(this.customfield_10099, { emitEvent: false });
    }

    onChangeAccessType(event) {
        this.form.markAsDirty();
        const _customfield_10098 = (<FormArray>this.form.get('customfield_10098')) as FormArray;

        if (event.checked) {
            _customfield_10098.push(new FormControl(event.source.value));
        } else {
            const i = _customfield_10098.controls.findIndex((x) => x.value === event.source.value);
            _customfield_10098.removeAt(i);
        }
    }

    listAllowedValues() {
        this.jiraService.listUtAllowedValues().subscribe((allowedValuesRes) => {
            this.initAllowedValuesDropDown(allowedValuesRes);
            this.maybeSetApprover.call(this);
        });
    }

    initAllowedValuesDropDown(allowedValues: ExposedJiraAllowedValuesFieldsDTORes) {
        this.systemDropDown = [];
        this.accessTypeDropDown = [];
        this.approverDropDown = [];

        allowedValues.fields.customfield_10068.allowedValues.forEach((value) => {
            this.systemDropDown.push({
                value: value.id,
                viewValue: value.value,
            });
        });
        allowedValues.fields.customfield_10098.allowedValues.forEach((value) => {
            this.accessTypeDropDown.push({
                value: value.id,
                viewValue: value.value,
            });
        });
        allowedValues.fields.customfield_10099.allowedValues.forEach((value) => {
            this.approverDropDown.push({
                value: value.id,
                viewValue: value.value,
            });
        });
    }

    redirectToJiraForm() {
        window.open(environment.jiraNewEmployeeAccessUrl, '_blank');
    }

    goToMyRequests() {
        this.router.navigate(['minar-beidnir'], { relativeTo: this.route });
    }

    goToAccessRequestForm() {
        this.router.navigate(['skra-beidni'], { relativeTo: this.route });
    }

    goToAccessRequestOverview() {
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    goToAssistsOverview() {
        this.router.navigate(['../yfirlit'], { relativeTo: this.route });
    }

    maybeSetApprover(this: AssistsJiraUtComponent) {
        if (this.userDetails && this.approverDropDown?.length) {
            this.findUsersSvidsstjori();
        }
    }

    formatDate(date) {
        return formatDate(date);
    }

    async submit() {
        this.form.markAllAsTouched();
        this.submitted = true;

        if (this.form.invalid) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                message: this.translationService.translate('text_confirm_send_request'),
            } as DialogConfirmData,
        });

        dialogRef.afterClosed().subscribe(async (confirm: boolean) => {
            if (!confirm) {
                return;
            }

            this.formEnabled = false;
            this.form.disable();

            try {
                const html = this.form.get('description')!.value ?? '';
                const adfJson = await this.htmlToAdf.convert(html);
                const description: JiraCreateIssueFieldsDescriptionDTOs = JSON.parse(adfJson);

                const createUtReq: JiraCreateIssueDTOReq = {
                    fields: {
                        description: description,
                        issueType: {
                            id: this.issueTypeId,
                        },
                        project: {
                            key: this.issueProjectKey,
                        },
                        reporter: null,
                        summary: this.form.get('summary').value,
                        customfield_10010: null,
                        customfield_10066: null,
                        customfield_10067: null,
                        customfield_10068: null,
                        customfield_10111: null,
                        customfield_10069: null,
                        customfield_10075: null,
                        customfield_10098: [],
                        customfield_10099: { id: this.customfield_10099 },
                        customfield_10078: null,
                        customfield_10079: null,
                        customfield_10081: null,
                        customfield_10085: null,
                    },
                };

                if (this.form.get('customfield_10068').value != '') {
                    createUtReq.fields.customfield_10068 = {
                        id: this.form.get('customfield_10068').value,
                    };
                }

                const customfield_10098 = (<FormArray>this.form.get('customfield_10098')) as FormArray;
                customfield_10098.controls.forEach((control) => {
                    createUtReq.fields.customfield_10098.push({
                        id: control.value,
                    });
                });

                this.jiraService.createUT(createUtReq, this.files).subscribe({
                    next: (issueKey) => {
                        if (!issueKey) {
                            this.alertsService.changealertHeader('Villa við skráningu beiðni');
                            this.alertsService.changeAlertErrorMessage('Tókst ekki að skrá beiðni. Reyndu aftur.');
                            this.alertsService.changeAlertErrorShow(true);

                            this.submitted = false;
                            this.formEnabled = true;
                            this.form.enable();
                            return;
                        }

                        this.handleSuccessfulSubmit(issueKey);
                    },
                    error: () => {
                        this.alertsService.changealertHeader('Villa við skráningu beiðni');
                        this.alertsService.changeAlertErrorMessage('Tókst ekki að skrá beiðni. Reyndu aftur síðar.');
                        this.alertsService.changeAlertErrorShow(true);

                        this.submitted = false;
                        this.formEnabled = true;
                        this.form.enable();
                    },
                });
            } catch (e) {
                this.alertsService.changealertHeader('Villa við breytingu lýsingar');
                this.alertsService.changeAlertErrorMessage('Tókst ekki að breyta yfir í ADF.');
                this.alertsService.changeAlertErrorShow(true);
                this.submitted = false;
                this.formEnabled = true;
                this.form.enable();
            }
        });
    }

    private handleSuccessfulSubmit(issueKey: string) {
        this.initForm();
        this.resetList();

        this.router.navigate(['../minar-beidnir'], {
            relativeTo: this.route,
            queryParams: {
                created: issueKey,
            },
        });
    }

    public descriptionOnChange({ editor }: ChangeEvent) {
        this.form.markAsDirty();
        this.descriptionTextHTML = editor.getData();
        this.form.get('description').setValue(editor.getData());
    }

    public onEditorReady(editor: any) {
        editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => new InlineBase64UploadAdapter(loader);
    }

    public editorConfig: any = {
        alignment: { options: ['left', 'right', 'center', 'justify'] },
        toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList', 'link', 'imageUpload', '|', 'undo', 'redo'],
        image: { toolbar: ['toggleImageCaption', 'imageTextAlternative'] },
    };

    //#region Table Sort and Filter

    searchFilter(filterValue: string) {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.displayedData.filter = filterValue;
    }

    sortData(sort: Sort) {
        const data = this.displayedData.data.slice();
        if (!sort.active || sort.direction === '') {
            this.displayedData.data = data;
            return;
        }

        this.displayedData.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'key':
                    return sortCompare(a.key, b.key, isAsc);
                case 'title':
                    return sortCompare(a.title, b.title, isAsc);
                case 'created':
                    return sortCompare(a.created, b.created, isAsc);
                case 'status':
                    return sortCompare(a.status, b.status, isAsc);
                default:
                    return 0;
            }
        });
    }

    // Empty the list and reset pagination
    resetList() {
        this.issues = [];
        this.nextPageToken = null;
        this.isLastPage = false;
        this.displayedData.data = this.issues;
        this.initialFetchComplete = false;
    }

    fetchIssues(ensureKey?: string) {
        this.isLoading = true;
        const currentSession = this.loadSessionId;

        const req: ExposedListJiraIssuesReq = {
            NextPageToken: this.nextPageToken,
            PageSize: this.pageSize,
            Project: 'UT',
            Ssn: null,
            EnsureKey: ensureKey ?? null,
        };

        this.jiraService.listIssues(req).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (currentSession !== this.loadSessionId) return;

                const mapped = this.mapIssues(res);

                this.issues = this.mergeByKey(this.issues, mapped);

                // Only show requests that are NOT New Employee Access requests
                this.issues = this.issues.filter((i) => i.requestTypeId != environment.jiraRequestTypeNewEmployeeAccess);

                this.displayedData.data = this.issues;
                this.isLastPage = res.isLast;
                this.nextPageToken = res.nextPageToken ?? null;
                this.initialFetchComplete = true;
                this.resetObserver();
            },
            error: () => {
                this.isLoading = false;

                this.alertsService.changealertHeader('Villa við að ná í lista');
                this.alertsService.changeAlertErrorMessage('Reyndu aftur síðar.');
                this.alertsService.changeAlertErrorShow(true);
            },
        });
    }

    //#endregion

    //#region Attachements

    onSelect(event) {
        this.files.push(...event.addedFiles);
    }

    onRemove(event) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    //#endregion

    revertAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.form.markAsPristine();
            this.form.reset();
            this.initForm();
        }
    }
}
